import { useEffect, useRef, useState } from 'react';
import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export type SessionPlayerCounts = {
  as_of: string;
  online_available: boolean;
  counts: Record<
    number,
    { players: number; active: number | null; online: number | null }
  >;
};

export default function useSessionPlayerCounts(
  sessionIds: number[],
  revision: number,
  pageLoading: boolean,
) {
  const initialRevision = useRef(revision).current;
  const ids = sessionIds.join(',');
  const page = `${revision}:${ids}`;
  const ready = !pageLoading && revision !== initialRevision && !!ids;
  const [refresh, setRefresh] = useState(0);
  const inFlight = useRef(false);
  const attempted = useRef('');
  const [state, setState] = useState<{
    page?: string;
    data?: SessionPlayerCounts;
    loading: boolean;
    error: boolean;
  }>({ loading: false, error: false });

  useEffect(() => {
    if (!ready) {
      setState(previous =>
        previous.loading ? { ...previous, loading: false } : previous,
      );
      return;
    }
    const request = `${page}:${refresh}`;
    if (attempted.current === request) return;
    attempted.current = request;
    const controller = new AbortController();
    inFlight.current = true;
    setState(previous => ({
      page,
      data: previous.page === page ? previous.data : undefined,
      loading: true,
      error: false,
    }));
    void apiCaller({
      apiUrl: API_URL,
      path: '/sessions/admin_player_counts/',
      data: { session_ids: ids },
      signal: controller.signal,
    })
      .then(response => {
        if (controller.signal.aborted) return;
        if (response.status !== 200) throw new Error('Counts unavailable');
        setState({ page, data: response.result, loading: false, error: false });
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setState(previous => ({ ...previous, loading: false, error: true }));
      })
      .finally(() => {
        if (!controller.signal.aborted) inFlight.current = false;
      });
    return () => {
      controller.abort();
      inFlight.current = false;
    };
  }, [ids, page, ready, refresh]);

  return {
    ready,
    data: state.page === page ? state.data : undefined,
    loading: ready && state.loading,
    error: state.page === page && state.error,
    refresh: () => {
      if (!ready || inFlight.current) return;
      inFlight.current = true;
      setRefresh(value => value + 1);
    },
  };
}
